import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";

import { ConfluenceLogo, JiraLogo, LoomLogo } from "@atlaskit/logo";
import {
    JiraLogo as JiraLogoNext,
    JiraProductDiscoveryLogo
} from "@atlaskit/logo-next";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import {
    ConfluenceBackground,
    JiraBackground,
    LoomBackground,
    JiraProductDiscoveryBackground,
    TwcBackground
} from "@atlassian/product-wireframes";
import { JiraServiceManagementBackground } from "@atlassian/product-wireframes/jira-service-management";
import { ServCoBackground } from "@atlassian/product-wireframes/service-collection";

import {
    ServiceCollectionLogoV2,
    TeamworkCollectionLogo
} from "@/components/signup/utils";
import { Picture } from "@/components/wpl/image/src";
import { ClientOnly } from "@/utils/client-only";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { Modal } from "./modal";
import { useIsJsmCrossflow } from "./use-is-jsm-crossflow";

// the `version` prop will be removed when cfind4831ProductOverlayModalVisualUpdates is productionized
export enum LayoutVersion {
    V1 = "V1",
    V2 = "V2",
    V3 = "V3"
}

type ProductOverlayTemplateProps = PropsWithChildren<{
    clientOnly?: boolean;
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
    productLogo?: ReactNode;
    productBackground: React.ComponentType<{ children: ReactNode }>;
    showBlueHeaderOnMobile?: boolean;
    showBrandFooter?: boolean;
    showProductLogoOnLargeScreens?: boolean;
    fadeOutContent?: boolean;
    version?: LayoutVersion;
    moveLogoToModal?: boolean;
}>;

// eslint-disable-next-line complexity
const ProductOverlayTemplate = ({
    children,
    clientOnly = true,
    header,
    subheader,
    size,
    moveLogoToModal,
    productLogo,
    productBackground,
    showBlueHeaderOnMobile = false,
    showBrandFooter = true,
    showProductLogoOnLargeScreens = false,
    fadeOutContent = false,
    version = LayoutVersion.V1
}: // eslint-disable-next-line complexity
ProductOverlayTemplateProps) => {
    const ProductBackground = productBackground;

    const template = (
        <ProductBackground>
            <Modal
                showBlueHeaderOnMobile={showBlueHeaderOnMobile}
                showProductLogoOnLargeScreens={showProductLogoOnLargeScreens}
                size={size}
                showBrandFooter={showBrandFooter}
                version={version}
                productLogo={productLogo}
                moveLogoToModal={moveLogoToModal}
            >
                {version === LayoutVersion.V2 ? (
                    <div css={LayoutV2.innerModalCss}>{children}</div>
                ) : version === LayoutVersion.V3 ? (
                    <div
                        css={[
                            LayoutV3.innerModalCss,
                            size === "sm" && LayoutV3.innerModalSmCss
                        ]}
                    >
                        {!moveLogoToModal && (
                            <div css={[productLogoContainerCss, hideAboveSm]}>
                                {productLogo}
                            </div>
                        )}
                        {header && (
                            <h1 css={[headerCssV2, fadeOutContent && fade]}>
                                {header}
                            </h1>
                        )}
                        {subheader && (
                            <p css={[subheaderCss, fadeOutContent && fade]}>
                                {subheader}
                            </p>
                        )}
                        {children}
                    </div>
                ) : (
                    <div css={innerModalCss}>
                        <div
                            css={[
                                productLogoContainerCss,
                                fadeOutContent && fade
                            ]}
                        >
                            {productLogo}
                        </div>
                        {header && (
                            <h1 css={[headerCss, fadeOutContent && fade]}>
                                {header}
                            </h1>
                        )}
                        {subheader && (
                            <p css={[subheaderCss, fadeOutContent && fade]}>
                                {subheader}
                            </p>
                        )}
                        {children}
                    </div>
                )}
            </Modal>
        </ProductBackground>
    );

    if (clientOnly) {
        return <ClientOnly>{template}</ClientOnly>;
    }

    return template;
};

export const ConfluenceOverlayTemplate = ({
    children,
    header,
    subheader,
    size
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
}>) => {
    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );
    return (
        <ProductOverlayTemplate
            productLogo={<ConfluenceLogo appearance="brand" size="medium" />}
            productBackground={ConfluenceBackground}
            header={header}
            subheader={subheader}
            size={size}
            showBlueHeaderOnMobile={!modalVisualUpdates}
            showBrandFooter={!modalVisualUpdates}
            clientOnly={false}
            version={modalVisualUpdates ? LayoutVersion.V3 : undefined}
        >
            {children}
        </ProductOverlayTemplate>
    );
};

// Backward compatibility: Keep the JiraOverlayTemplate as a wrapper
export const JiraOverlayTemplate = ({
    children,
    header,
    subheader,
    size,
    fadeOutContent,
    version
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
    fadeOutContent?: boolean;
    version?: LayoutVersion;
}>) => {
    // for team26, opt all appearances into V3
    version = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    )
        ? LayoutVersion.V3
        : version;
    return (
        <ProductOverlayTemplate
            productLogo={
                version === LayoutVersion.V2 ? undefined : version ===
                  LayoutVersion.V3 ? (
                    <JiraLogoNext
                        size="small"
                        appearance="brand"
                        shouldUseNewLogoDesign
                    />
                ) : (
                    <JiraLogo size="small" appearance="brand" />
                )
            }
            productBackground={JiraBackground}
            header={header}
            subheader={subheader}
            size={size}
            clientOnly={false}
            fadeOutContent={fadeOutContent}
            version={version}
            showBrandFooter={
                version === LayoutVersion.V2 || version === LayoutVersion.V3
                    ? false
                    : true
            }
        >
            {children}
        </ProductOverlayTemplate>
    );
};

// Loom overlay template using the parameterized component
export const LoomOverlayTemplate = ({
    children,
    header,
    subheader,
    size
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
}>) => {
    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );
    return (
        <ProductOverlayTemplate
            productLogo={<LoomLogo size="small" appearance="brand" />}
            productBackground={LoomBackground}
            header={header}
            subheader={subheader}
            size={size}
            showBrandFooter={!modalVisualUpdates}
            version={modalVisualUpdates ? LayoutVersion.V3 : undefined}
        >
            {children}
        </ProductOverlayTemplate>
    );
};

// Pin the JSM wireframe to the viewport so it doesn't scroll with the modal
// content. The Blanket (modal) already uses position: fixed and renders on top.
// The wireframe is rendered by `@atlassian/product-wireframes` as a sibling to
// the modal, so we must target it via its testId since the package exposes no
// other hook.
const fixedWireframeBackgroundCss = css({
    [media.above.sm]: {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        '& > [data-testid="above-small-screen"]': {
            position: "fixed",
            inset: 0
        }
    }
});

const FixedServCoBackground = ({ children }: { children: ReactNode }) => (
    <div css={fixedWireframeBackgroundCss}>
        <ServCoBackground>{children}</ServCoBackground>
    </div>
);

const FixedJsmBackground = ({ children }: { children: ReactNode }) => (
    <div css={fixedWireframeBackgroundCss}>
        <JiraServiceManagementBackground>
            {children}
        </JiraServiceManagementBackground>
    </div>
);

// Service Collection signup overlay. Background reflects the entry point:
//   - From a JSM page (see useIsJsmCrossflow) → JiraServiceManagementBackground
//   - Otherwise (direct Service Collection entry) → ServCoBackground
export const JSMOverlayTemplate = ({
    children,
    header,
    subheader,
    size,
    hideLogo = false
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
    hideLogo?: boolean;
}>) => {
    const isJsmCrossflow = useIsJsmCrossflow();
    const productBackground = isJsmCrossflow
        ? FixedJsmBackground
        : FixedServCoBackground;
    return (
        <ProductOverlayTemplate
            productLogo={hideLogo ? undefined : <ServiceCollectionLogoV2 />}
            productBackground={productBackground}
            header={header}
            subheader={subheader}
            size={size}
            version={LayoutVersion.V3}
            showProductLogoOnLargeScreens={true}
            moveLogoToModal={true}
            // todo sync version and showBrandFooter when experiment finishes
            // https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/gwp_9785_servco_lofi_experiment
        >
            {children}
        </ProductOverlayTemplate>
    );
};

// JPD overlay template using the parameterized component
export const JPDOverlayTemplate = ({
    children,
    header,
    subheader,
    size,
    showBrandFooter = true
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
    showBrandFooter?: boolean;
}>) => {
    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );
    const productLogo = modalVisualUpdates ? (
        <JiraProductDiscoveryLogo
            size="small"
            appearance="brand"
            shouldUseNewLogoDesign
        />
    ) : (
        <Picture
            image={{
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/108wt817r553ywiinjjke21wmaoi4l2a.webp",
                alt: "Jira Product Discovery",
                width: 246,
                height: 24
            }}
        />
    );
    return (
        // TODO: once refreshed logos are made avaliable in WAC, update this to use the new @atlaskit/logo
        <ProductOverlayTemplate
            productLogo={productLogo}
            productBackground={JiraProductDiscoveryBackground}
            header={header}
            subheader={subheader}
            size={size}
            showBrandFooter={!modalVisualUpdates && showBrandFooter}
            version={modalVisualUpdates ? LayoutVersion.V3 : undefined}
        >
            {children}
        </ProductOverlayTemplate>
    );
};

// TWC overlay template using the parameterized component
export const TwcOverlayTemplate = ({
    children,
    header,
    subheader,
    size
}: PropsWithChildren<{
    header?: string;
    subheader?: string;
    size?: "sm" | "lg";
}>) => (
    // TODO: once refreshed logos are made avaliable in WAC, update this to use the new @atlaskit/logo
    <ProductOverlayTemplate
        productBackground={TwcBackground}
        header={header}
        subheader={subheader}
        size={size}
        productLogo={<TeamworkCollectionLogo shouldShowBrand={false} />}
        showBrandFooter={true}
        showProductLogoOnLargeScreens={true}
        version={LayoutVersion.V3}
        moveLogoToModal={true}
    >
        {children}
    </ProductOverlayTemplate>
);

const hideAboveSm = css({
    [media.above.sm]: {
        display: "none !important"
    }
});

const productLogoContainerCss = css({
    display: "flex",
    justifyContent: "center",
    marginBlockStart: "8px",
    marginBlockEnd: "16px"
});

const headerCss = css({
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.2,
    margin: "24px 0 0",
    textAlign: "center"
});

const headerCssV2 = css({
    color: token("color.text"),
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.2,
    margin: "0",
    textAlign: "center"
});

const subheaderCss = css({
    margin: "8px 0 24px",
    textAlign: "center"
});

const fade = css({
    opacity: 0,
    transition: "opacity 0.5s ease-in-out"
});

const innerModalCss = css({
    alignItems: "stretch",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    [media.above.sm]: {
        padding: "40px 80px 80px"
    }
});

const LayoutV2 = {
    innerModalCss: css({
        position: "relative",
        alignItems: "stretch",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: 640,
        [media.above.sm]: {
            padding: token("space.800", "64px")
        },
        overflow: "hidden"
    })
};

const LayoutV3 = {
    innerModalCss: css({
        position: "relative",
        alignItems: "stretch",
        color: token("color.text.subtle"),
        display: "flex",
        flexDirection: "column",
        fontFamily: "var(--atlassian-sans-font-family)",
        margin: "0 auto",
        width: "100%",
        minHeight: 560,
        [media.above.sm]: {
            padding: token("space.800", "64px")
        },
        overflow: "hidden"
    }),
    innerModalSmCss: css({
        maxWidth: 432,
        [media.above.sm]: {
            maxWidth: 560
        }
    })
};
